import { Vector3 } from 'three';

/**
 * Pinhole camera intrinsics in video-pixel units.
 *
 * The real focal length of a phone's front camera is unknown to the browser
 * (getUserMedia does not expose it), so we assume a field of view on the long
 * side of the sensor and derive the focal length. Important property of the
 * pose pipeline: the *on-screen size* of the frame does not depend on this
 * assumption to within ~1 % (see FramePoseSolver); only the perspective
 * foreshortening of the temples depends on it. Adjustable live in the debug panel.
 */
export interface CameraIntrinsics {
  width: number;
  height: number;
  /** Focal length in pixels (fx = fy assumed: square pixels). */
  f: number;
  /** Principal point (image centre assumed). */
  cx: number;
  cy: number;
}

/** Typical smartphone front cameras: 60–75° on the long side. */
export const DEFAULT_LONG_SIDE_FOV_DEG = 65;

export function intrinsicsFromFov(width: number, height: number, longSideFovDeg = DEFAULT_LONG_SIDE_FOV_DEG): CameraIntrinsics {
  const longSide = Math.max(width, height);
  const f = longSide / 2 / Math.tan((longSideFovDeg * Math.PI) / 360);
  return { width, height, f, cx: width / 2, cy: height / 2 };
}

/** Vertical field of view in degrees, as needed by THREE.PerspectiveCamera. */
export function verticalFovDeg(k: CameraIntrinsics): number {
  return (2 * Math.atan(k.height / 2 / k.f) * 180) / Math.PI;
}

/**
 * Back-projects a pixel (u right, v down) at metric depth `depthMm` into camera
 * space (X right, Y up, Z towards the viewer → the point has Z = -depth).
 */
export function unproject(k: CameraIntrinsics, u: number, v: number, depthMm: number, out = new Vector3()): Vector3 {
  const x = ((u - k.cx) * depthMm) / k.f;
  const y = (-(v - k.cy) * depthMm) / k.f;
  return out.set(x, y, -depthMm);
}

/** Projects a camera-space point (Z negative in front of the camera) to pixels. */
export function project(k: CameraIntrinsics, p: Vector3): { u: number; v: number } {
  const depth = -p.z;
  return { u: k.cx + (p.x * k.f) / depth, v: k.cy - (p.y * k.f) / depth };
}
